// @ts-strict-ignore
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import {
    selectAppPlatform,
    selectRequiredSteps,
    selectAppStoreType,
    selectShouldHideNonBlockingSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectReleaseStepsByReleaseType,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppSettingsUpdatable } from '../../actions/updatable';
import {
    isNonBlockingStepForReleaseType,
    getReleaseTypeFromReleaseTypeForNonBlockingSteps,
    populateUndefinedNonBlockingStepsConfig,
} from '../../helpers';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { antGrey } from '../styles/globalStyles';
import { ReleaseStepType, ReleaseTypesForNonBlockingSteps, ReleaseStepBlockingConfig } from '../../types';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { globalMessage } from '../../utils/globalMessage';
import Switch from 'antd/lib/switch';
import Divider from 'antd/lib/divider';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import { cloneDeep } from 'lodash';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface NonBlockingReleaseStepsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const NonBlockingReleaseStepsModal: React.FC<NonBlockingReleaseStepsModalProps> = (
    props: NonBlockingReleaseStepsModalProps,
) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const requiredSteps = useSelector((state: RootState) => selectRequiredSteps(state, props.appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, props.appId),
    );

    const releaseStepsByReleaseType = useSelector((state: RootState) =>
        selectReleaseStepsByReleaseType(state, props.appId),
    );

    const editAppSettingsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateNonBlockingSteps);
    const editAppSettingsError = useError(AppSettingsUpdatable.UpdateNonBlockingSteps);
    const editAppSettingsSuccess = useUpdated(AppSettingsUpdatable.UpdateNonBlockingSteps);

    const editAppSettings = (values: { [key in ReleaseStepType]: boolean | { [key in string]: boolean } }) => {
        const nonBlockingStepsConfig = populateUndefinedNonBlockingStepsConfig(
            nonBlockingStepsConfigMapValues,
            releaseStepsByReleaseType,
        );

        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateNonBlockingSteps, {
                orgId: props.orgId,
                appId: props.appId,
                releaseStepsBlockingConfigForReleaseType: nonBlockingStepsConfig,
                shouldHideNonBlockingSteps: values['hideInactiveSteps'],
            }),
        );
    };

    type ReleaseTypeItem = {
        value: string;
        label: string;
    };

    const items: ReleaseTypeItem[] = [
        {
            value: ReleaseTypesForNonBlockingSteps.Standard,
            label: 'Standard',
        },
        {
            value: ReleaseTypesForNonBlockingSteps.Hotfix,
            label: 'Hotfixes',
        },
        {
            value: ReleaseTypesForNonBlockingSteps.Rollback,
            label: 'Rollbacks',
        },
    ];

    const handleReleaseTypesMenuSelection = (
        value: ReleaseTypesForNonBlockingSteps,
        stepsConfig: Record<ReleaseStepType, ReleaseStepBlockingConfig>,
    ) => {
        setSelectedNonBlockingStepsReleaseType(value as ReleaseTypesForNonBlockingSteps);
        const formStepsConfig = Object.keys(stepsConfig).reduce((obj, step) => {
            obj[step] = stepsConfig[step] == ReleaseStepBlockingConfig.Blocking;
            return obj;
        }, {});
        form.setFieldsValue(formStepsConfig);
    };

    const [selectedNonBlockingStepsReleaseType, setSelectedNonBlockingStepsReleaseType] = React.useState(
        ReleaseTypesForNonBlockingSteps.Standard,
    );
    const selectedReleaseType = getReleaseTypeFromReleaseTypeForNonBlockingSteps(selectedNonBlockingStepsReleaseType);

    const [nonBlockingStepsConfigMapValues, setNonBlockingStepsConfigMapValues] = React.useState<
        Record<ReleaseTypesForNonBlockingSteps, Record<ReleaseStepType, ReleaseStepBlockingConfig>>
    >(cloneDeep(nonBlockingStepsConfigMap));

    const handleChangeInBlockingConfig = (checked: boolean, step: ReleaseStepType) => {
        const blockingConfig = checked ? ReleaseStepBlockingConfig.Blocking : ReleaseStepBlockingConfig.NonBlocking;
        const updatedData = { ...nonBlockingStepsConfigMapValues };
        updatedData[selectedNonBlockingStepsReleaseType][step] = blockingConfig;
        setNonBlockingStepsConfigMapValues(updatedData);
    };

    React.useEffect(() => {
        if (editAppSettingsError) {
            globalMessage.error(editAppSettingsError);
        }
    }, [editAppSettingsError]);

    React.useEffect(() => {
        if (editAppSettingsSuccess) {
            globalMessage.success('Required release steps updated!');

            props.onCancel();
        }
    }, [editAppSettingsSuccess]);

    return (
        <Modal
            title="Edit required release steps"
            okButtonProps={{ loading: editAppSettingsIsLoading }}
            onOk={() => form.submit()}
            okText="Save"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose={true}
            afterClose={() => {
                form.resetFields();
                setNonBlockingStepsConfigMapValues(cloneDeep(nonBlockingStepsConfigMap));
                handleReleaseTypesMenuSelection(
                    ReleaseTypesForNonBlockingSteps.Standard,
                    cloneDeep(nonBlockingStepsConfigMap[ReleaseTypesForNonBlockingSteps.Standard]),
                );
            }}
            bodyStyle={{ maxHeight: 500, overflowY: 'scroll' }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Form
                    onFinish={editAppSettings}
                    style={{ width: '100%' }}
                    form={form}
                    colon={false}
                    initialValues={{
                        hideInactiveSteps: useSelector((state: RootState) =>
                            selectShouldHideNonBlockingSteps(state, props.appId),
                        ),
                    }}
                    layout="vertical"
                    size="small"
                >
                    <Form.Item
                        name="hideInactiveSteps"
                        valuePropName="checked"
                        help="This setting will apply to all release types configured below."
                    >
                        <Checkbox>Hide steps in the release timeline that are not required</Checkbox>
                    </Form.Item>
                    <Divider />
                    <Form.Item
                        name="releaseType"
                        valuePropName="selected"
                        label="Release type"
                        help={
                            selectedNonBlockingStepsReleaseType === ReleaseTypesForNonBlockingSteps.Standard ?
                                'All releases except hotfixes or rollbacks'
                            :   ''
                        }
                    >
                        <Select
                            options={items}
                            defaultValue={selectedNonBlockingStepsReleaseType}
                            onChange={(value) => {
                                handleReleaseTypesMenuSelection(
                                    value as ReleaseTypesForNonBlockingSteps,
                                    nonBlockingStepsConfigMapValues[value],
                                );
                            }}
                        ></Select>
                    </Form.Item>
                    <Form.Item>
                        <Text>
                            Choose which steps will gate app submission and release for{' '}
                            {selectedNonBlockingStepsReleaseType} releases.
                        </Text>
                    </Form.Item>
                    {(releaseStepsByReleaseType[selectedReleaseType] ?? []).map((step: ReleaseStepType) => {
                        const isRequiredStep = requiredSteps.includes(step);
                        const isNonBlocking = isNonBlockingStepForReleaseType(
                            nonBlockingStepsConfigMapValues[selectedNonBlockingStepsReleaseType],
                            step,
                        );
                        const label = `${displayTextForReleaseStepType(step, appPlatform, appStoreType, isWorkflowsCIStepEnabled)}
                                ${requiredSteps.includes(step) ? '(always required)' : ''}`;
                        return (
                            <div
                                key={step}
                                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}
                            >
                                <Text style={isRequiredStep ? { color: antGrey.grey7 } : undefined}>{label}</Text>
                                <Form.Item
                                    name={step}
                                    key={step}
                                    valuePropName="checked"
                                    label={null}
                                    noStyle
                                    initialValue={!isNonBlocking}
                                >
                                    <Switch
                                        disabled={isRequiredStep}
                                        onChange={(checked) => handleChangeInBlockingConfig(checked, step)}
                                        style={{ justifySelf: 'flex-end' }}
                                    />
                                </Form.Item>
                            </div>
                        );
                    })}
                </Form>
            </div>
        </Modal>
    );
};

export default NonBlockingReleaseStepsModal;
